import React from 'react'
import { string, shape, array, bool, object } from 'prop-types'
import { graphql } from 'gatsby'
import 'intersection-observer'

import Product from '@/components/Product'
import SEO from '@/components/SEO'
import Layout from '@/layout'
import mediaQueries from '@/layout/media-queries'
import { getSeoTitle, getSeoDesc } from '@/utils/products'
import { OnboardingProvider } from '@/context/OnboardingContext'
import EnhancedListProvider from '@/context/EnhancedList'
import Sidebar from '@/components/Services/Sidebar'
import { FILTERS } from '@/constants'

const overrideStyles = {
  container: {
    marginBottom: '60px',

    [mediaQueries.smallScreen()]: {
      marginBottom: '0px',
    },
  },
}

const Service = ({ data: { service, developer } }) => {
  if (!service) return null

  const { id, name, shortDescription, screenshots, hidden, slug } = service

  const filterServicesOptions = Object.values(FILTERS.SERVICES.OPTIONS)

  const defaultFilters = { [FILTERS.SERVICES.ID]: [] }

  const defaultFiltersOptions = {
    [FILTERS.SERVICES.ID]: filterServicesOptions,
  }

  const supportedFilters = [FILTERS.SERVICES.ID]

  return (
    <EnhancedListProvider
      defaultFilters={defaultFilters}
      defaultFiltersOptions={defaultFiltersOptions}
      supportedFilters={supportedFilters}
    >
      <OnboardingProvider id={id}>
        <Layout
          service={service}
          headerLabel={service.name}
          overrideStyles={overrideStyles}
        >
          <SEO
            title={getSeoTitle(name)}
            description={getSeoDesc(slug) || shortDescription}
            type="LiveChat Service"
            typeLabel="Type"
            schemaMarkup={`{
            "@context": "http://schema.org/",
            "@type": "SoftwareApplication",
            "name": "${name} for LiveChat",
            "image": "${
              screenshots && screenshots.length ? screenshots[0].large : ''
            }",
            "description": "${shortDescription}",
            "applicationCategory": "Add-ons",
            "applicationSuite": "LiveChat"
          }`}
            path={`/services/${slug}/`}
          >
            {hidden && <meta name="robots" content="noindex, nofollow" />}
          </SEO>

          <Product
            product={service}
            sidebarComponent={Sidebar}
            developer={developer}
          />
        </Layout>
      </OnboardingProvider>
    </EnhancedListProvider>
  )
}

Service.propTypes = {
  data: shape({
    service: shape({
      id: string,
      name: string,
      shortDescription: string,
      screenshots: array,
      hidden: bool,
      slug: string,
    }),
    developer: object,
  }),
}

export default Service

export const query = graphql`
  query ($appId: String!, $ownerId: String!) {
    service(id: { eq: $appId, ne: "service-placeholder" }) {
      id
      slug
      name
      hidden
      shortDescription
      longDescription
      ownerId
      screenshots {
        id
        large
        original
        small
        title
      }
      tutorialUrl
      privacyPolicyUrl
      icons {
        small
        large
      }
      payment {
        price
        frequency
        trialDays
        perAccount
      }
      features {
        description
        title
      }
      benefits {
        description
        title
      }
      category {
        name
        slug
      }
      customProps {
        usingBeta
        appVideoUrl
        inAppPurchases
        installationsOffset
        isPremium
        paymentFrequency
      }
      installations
      elements {
        cover {
          title
          image
          color
        }
      }
    }
    developer(id: { eq: $ownerId }) {
      name
      company
      slug
      contactEmail
      apps {
        icons {
          id
          large
          original
          small
          title
        }
        id
        name
        payment {
          creditCardRequired
          frequency
          perAccount
          price
          trialDays
        }
        shortDescription
        slug
      }
      services {
        icons {
          id
          large
          original
          small
          title
        }
        id
        name
        payment {
          creditCardRequired
          frequency
          perAccount
          price
          trialDays
        }
        shortDescription
        slug
      }
    }
  }
`
